/**
 * NotificationBell — Topbar bell icon with unread badge + dropdown.
 * Polls unread-count every 30s; fetches items on open.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuth = () => ({
  headers: {
    Authorization: `Bearer ${
      localStorage.getItem('cop31_token') ||
      localStorage.getItem('cop31_ultra_token') ||
      localStorage.getItem('cop31_supplier_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      ''
    }`,
  },
});

function timeAgo(iso, tr) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)     return tr ? 'az önce'    : 'just now';
  if (diff < 3600)   return tr ? `${Math.floor(diff/60)} dk önce`  : `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)  return tr ? `${Math.floor(diff/3600)} sa önce`: `${Math.floor(diff/3600)}h ago`;
  if (diff < 604800) return tr ? `${Math.floor(diff/86400)} gün önce` : `${Math.floor(diff/86400)}d ago`;
  return d.toLocaleDateString(tr ? 'tr-TR' : 'en-US');
}

export default function NotificationBell({ language = 'en', onNavigate, className = '', variant = 'light' }) {
  const tr = language === 'tr';
  const iconClass = variant === 'dark' ? 'text-gray-700' : 'text-white';
  const btnHoverClass = variant === 'dark' ? 'hover:bg-gray-100' : 'hover:bg-white/10';
  const [open, setOpen]         = useState(false);
  const [unread, setUnread]     = useState(0);
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const containerRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${BASE}/notifications/unread-count`, getAuth());
      setUnread(res.data?.unread || 0);
    } catch {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      // Pass current language so the backend re-renders i18n notifications
      // in the user's *current* language (not the language at create time).
      const res = await axios.get(`${BASE}/notifications/?limit=15&lang=${language}`, getAuth());
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch items whenever the user toggles language while the dropdown is
  // open — keeps the visible list in sync with the rest of the UI.
  // (`fetchItems` doesn't need to be in deps since it doesn't capture
  //  any state besides `language` itself.)
  useEffect(() => {
    if (open) fetchItems();
  }, [language]);  // eslint-disable-line

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchItems();
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${BASE}/notifications/${id}/read`, {}, getAuth());
      setItems(prev => prev.map(x => x.id === id ? { ...x, is_read: true } : x));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${BASE}/notifications/mark-all-read`, {}, getAuth());
      setItems(prev => prev.map(x => ({ ...x, is_read: true })));
      setUnread(0);
    } catch {}
  };

  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.link && onNavigate) onNavigate(n.link, n);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid="notif-bell-container">
      <button
        type="button"
        onClick={toggleOpen}
        className={`relative p-2 rounded-full transition-colors ${btnHoverClass}`}
        data-testid="notif-bell-btn"
        aria-label={tr ? 'Bildirimler' : 'Notifications'}
      >
        <Bell className={`h-5 w-5 ${iconClass}`} />
        {unread > 0 && (
          <span
            data-testid="notif-unread-badge"
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white/20"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 mt-2 w-96 max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-700" />
              <span className="text-sm font-bold text-gray-800">{tr ? 'Bildirimler' : 'Notifications'}</span>
              {unread > 0 && (
                <span className="text-[10px] bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full">{unread} {tr ? 'yeni' : 'new'}</span>
              )}
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                data-testid="notif-mark-all-read-btn"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {tr ? 'Tümünü okundu yap' : 'Mark all read'}
              </button>
            )}
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400">{tr ? 'Yükleniyor...' : 'Loading...'}</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-10 w-10 mx-auto text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">{tr ? 'Henüz bildiriminiz yok.' : 'No notifications yet.'}</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-b-0 transition-colors flex items-start gap-3 ${
                    n.is_read ? 'bg-white hover:bg-gray-50' : 'bg-emerald-50/60 hover:bg-emerald-50'
                  }`}
                >
                  <span
                    className={`mt-1.5 inline-block h-2 w-2 rounded-full shrink-0 ${
                      n.is_read ? 'bg-gray-300' : 'bg-emerald-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.is_read ? 'text-gray-600 font-medium' : 'text-gray-900 font-bold'}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at, tr)}</p>
                  </div>
                  {!n.is_read && (
                    <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-1" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
